import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import rushLogo from './rush-logo.png';
import { RushThemeToggle } from './rushTheme';
import useLocalePath from '../../i18n/useLocalePath';
import LocaleSwitcher from './LocaleSwitcher';

// Maps Rush design's HTML links to our React Router paths.
// Keep the file names from the design as map keys so other ported pages can reuse this.
export const HREF_MAP = {
  'Rush Studio Landing.html': '/',
  'Features.html': '/features',
  'Languages.html': '/languages',
  'Integrations.html': '/integrations',
  'Pricing.html': '/pricing',
  'Customers.html': '/customers',
  'Blog.html': '/blog',
  'About.html': '/about',
  'Careers.html': '/careers',
  'Contact.html': '/contact',
  'Security.html': '/security',
  'Privacy.html': '/privacy',
  'Terms.html': '/terms',
  'GDPR.html': '/gdpr',
  'AI Data.html': '/ai-data',
  'Sign In.html': '/login',
  'Sign Up.html': '/register',
  'Role Technical Writers.html': '/roles/technical-writers',
  'Role Marketing.html': '/roles/marketing',
  'Role Localization Managers.html': '/roles/localization-managers',
  'Role Product Managers.html': '/roles/product-managers',
  'Role Design Ops.html': '/roles/design-ops',
};

export const RushNav = ({ active }) => {
  const { t } = useTranslation('landing');
  const lp = useLocalePath();
  return (
    <header className="rs-nav">
      <div className="rs-container">
        <div className="rs-nav-inner">
          <Link to={lp('/')} className="rs-logo rs-logo-img" aria-label="Rush Studio home">
            {/* LCP image: served from a stable public path (matches the <link rel="preload">
                in index.html) and marked high-priority so it's fetched first. */}
            <img
              src={`${process.env.PUBLIC_URL}/rush-logo.png`}
              alt="Rush Studio"
              fetchpriority="high"
              decoding="async"
              width="212"
              height="98"
            />
          </Link>
          <nav className="rs-nav-links">
            <div className="rs-nav-drop">
              <button type="button" className={`rs-nav-trigger ${active === 'features' ? 'active' : ''}`} aria-haspopup="true">
                {t('nav.features')}
                <svg className="caret" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="rs-nav-menu is-left">
                <div className="rs-nav-menu-col">
                  <div className="rs-nav-menu-label">{t('nav.featuresMenu.overview')}</div>
                  <Link className="rs-nav-menu-item" to={lp('/features')}>
                    <span className="nm-icon">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2" y="2" width="4.4" height="4.4" rx="1" stroke="currentColor" strokeWidth="1.4" /><rect x="7.6" y="2" width="4.4" height="4.4" rx="1" stroke="currentColor" strokeWidth="1.4" /><rect x="2" y="7.6" width="4.4" height="4.4" rx="1" stroke="currentColor" strokeWidth="1.4" /><rect x="7.6" y="7.6" width="4.4" height="4.4" rx="1" stroke="currentColor" strokeWidth="1.4" /></svg>
                    </span>
                    <div className="nm-body">
                      <span className="nm-title">{t('nav.featuresMenu.allFeatures.title')}</span>
                      <span className="nm-sub">{t('nav.featuresMenu.allFeatures.sub')}</span>
                    </div>
                  </Link>
                  <div className="rs-nav-menu-cta">
                    <div className="nm-cta-title">{t('nav.featuresMenu.apart.title')}</div>
                    <div className="nm-cta-sub">{t('nav.featuresMenu.apart.sub')}</div>
                    <Link className="nm-cta-link" to={lp('/features')}>{t('nav.featuresMenu.apart.link')}</Link>
                  </div>
                </div>
                <div className="rs-nav-menu-col">
                  <div className="rs-nav-menu-label">{t('nav.featuresMenu.deepDives')}</div>
                  <Link className="rs-nav-menu-item" to={lp('/features/brand-voice')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 5.5v3M5.5 3.5v7M8.5 4.5v5M11.5 2.5v9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.featuresMenu.brandVoice.title')}</span><span className="nm-sub">{t('nav.featuresMenu.brandVoice.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/features/automations')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="2" width="5" height="3.6" rx="1" stroke="currentColor" strokeWidth="1.3" /><rect x="7.5" y="8.4" width="5" height="3.6" rx="1" stroke="currentColor" strokeWidth="1.3" /><path d="M6.5 3.8h2a1.5 1.5 0 011.5 1.5v3.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.featuresMenu.automations.title')}</span><span className="nm-sub">{t('nav.featuresMenu.automations.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/features/ai-quality')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 10a5 5 0 0110 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /><path d="M7 10l2.6-2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.featuresMenu.aiQuality.title')}</span><span className="nm-sub">{t('nav.featuresMenu.aiQuality.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/features/preview')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1.5 7s2.2-3.5 5.5-3.5S12.5 7 12.5 7 10.3 10.5 7 10.5 1.5 7 1.5 7z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><circle cx="7" cy="7" r="1.5" stroke="currentColor" strokeWidth="1.3" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.featuresMenu.preview.title')}</span><span className="nm-sub">{t('nav.featuresMenu.preview.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/features/layout-risk')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2l5.5 10h-11L7 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M7 6v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /><circle cx="7" cy="10.6" r="0.5" fill="currentColor" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.featuresMenu.layoutRisk.title')}</span><span className="nm-sub">{t('nav.featuresMenu.layoutRisk.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/features/resonance')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="1.4" stroke="currentColor" strokeWidth="1.4" /><path d="M3.8 3.8a4.5 4.5 0 000 6.4M10.2 3.8a4.5 4.5 0 010 6.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.featuresMenu.resonance.title')}</span><span className="nm-sub">{t('nav.featuresMenu.resonance.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/features/autopilot')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M8 1.5L3 8h3.2l-1 4.5L12 6H8.4l1-4.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.featuresMenu.autopilot.title')}</span><span className="nm-sub">{t('nav.featuresMenu.autopilot.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/features/file-types')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2l5 2.5-5 2.5-5-2.5L7 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M2 7l5 2.5L12 7M2 9.5L7 12l5-2.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.featuresMenu.fileTypes.title')}</span><span className="nm-sub">{t('nav.featuresMenu.fileTypes.sub')}</span></div>
                  </Link>
                </div>
              </div>
            </div>
            <Link to={lp('/languages')} className={active === 'languages' ? 'active' : ''}>{t('nav.languages')}</Link>
            <Link to={lp('/integrations')} className={active === 'integrations' ? 'active' : ''}>{t('nav.integrations')}</Link>
            <Link to={lp('/pricing')} className={active === 'pricing' ? 'active' : ''}>{t('nav.pricing')}</Link>
            <div className="rs-nav-drop">
              <button type="button" className={`rs-nav-trigger ${active === 'customers' ? 'active' : ''}`} aria-haspopup="true">
                {t('nav.customers')}
                <svg className="caret" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="rs-nav-menu">
                <div className="rs-nav-menu-col">
                  <div className="rs-nav-menu-label">{t('nav.customersMenu.overview')}</div>
                  <Link className="rs-nav-menu-item" to={lp('/customers')}>
                    <span className="nm-icon">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="5" r="2.4" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M2 12c.7-2.4 2.6-3.4 5-3.4s4.3 1 5 3.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                    <div className="nm-body">
                      <span className="nm-title">{t('nav.customersMenu.earlyAccess.title')}</span>
                      <span className="nm-sub">{t('nav.customersMenu.earlyAccess.sub')}</span>
                    </div>
                  </Link>
                  <div className="rs-nav-menu-cta">
                    <div className="nm-cta-title">{t('nav.customersMenu.launching.title')}</div>
                    <div className="nm-cta-sub">{t('nav.customersMenu.launching.sub')}</div>
                    <Link className="nm-cta-link" to={lp('/customers')}>{t('nav.customersMenu.launching.link')}</Link>
                  </div>
                </div>
                <div className="rs-nav-menu-col">
                  <div className="rs-nav-menu-label">{t('nav.customersMenu.byRole')}</div>
                  <Link className="rs-nav-menu-item" to={lp('/roles/technical-writers')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 2h6l2 2v8H3z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M5 6h4M5 8.5h4M5 11h2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.customersMenu.technicalWriters.title')}</span><span className="nm-sub">{t('nav.customersMenu.technicalWriters.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/roles/marketing')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 5.5L10 3v8L2.5 8.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M4 8.5v2.5h2V9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.customersMenu.marketing.title')}</span><span className="nm-sub">{t('nav.customersMenu.marketing.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/roles/localization-managers')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" /><path d="M2 7h10M7 2c1.6 1.5 1.6 8.5 0 10M7 2c-1.6 1.5-1.6 8.5 0 10" stroke="currentColor" strokeWidth="1.4" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.customersMenu.localizationManagers.title')}</span><span className="nm-sub">{t('nav.customersMenu.localizationManagers.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/roles/product-managers')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2" y="3" width="10" height="8" rx="1.4" stroke="currentColor" strokeWidth="1.4" /><path d="M2 6h10M5 3v8" stroke="currentColor" strokeWidth="1.4" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.customersMenu.productManagers.title')}</span><span className="nm-sub">{t('nav.customersMenu.productManagers.sub')}</span></div>
                  </Link>
                  <Link className="rs-nav-menu-item" to={lp('/roles/design-ops')}>
                    <span className="nm-icon"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5L11.5 4v6L7 12.5 2.5 10V4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M7 1.5v11M2.5 4L11.5 10M11.5 4L2.5 10" stroke="currentColor" strokeWidth="1.4" /></svg></span>
                    <div className="nm-body"><span className="nm-title">{t('nav.customersMenu.designOps.title')}</span><span className="nm-sub">{t('nav.customersMenu.designOps.sub')}</span></div>
                  </Link>
                </div>
              </div>
            </div>
            <Link to={lp('/blog')} className={active === 'blog' ? 'active' : ''}>{t('nav.blog')}</Link>
            <Link to={lp('/contact')} className={active === 'contact' ? 'active' : ''}>{t('nav.contact')}</Link>
          </nav>
          <div className="rs-nav-cta">
            <LocaleSwitcher />
            <RushThemeToggle />
            <Link className="btn btn-ghost-quiet btn-sm" to={lp('/login')}>{t('nav.signIn')}</Link>
            <Link className="btn btn-primary btn-sm" to={lp('/register')}>{t('nav.signUp')}</Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export const RushFooter = () => {
  const { t } = useTranslation('landing');
  const lp = useLocalePath();
  return (
  <footer className="rs-footer">
    <div className="rs-container">
      <div className="rs-footer-grid">
        <div>
          <Link to={lp('/')} className="rs-logo rs-logo-img" style={{ marginBottom: 12 }} aria-label="Rush Studio">
            <img src={rushLogo} alt="Rush Studio" />
          </Link>
          <p style={{ color: 'var(--ink-3)', fontSize: 13, maxWidth: 280, margin: 0 }}>
            {t('footer.tagline')}
          </p>
        </div>
        <div>
          <h5>{t('footer.product')}</h5>
          <ul>
            <li><Link to={lp('/')}>{t('footer.dashboard')}</Link></li>
            <li><Link to={lp('/features')}>{t('nav.features')}</Link></li>
            <li><Link to={lp('/languages')}>{t('nav.languages')}</Link></li>
            <li><Link to={lp('/integrations')}>{t('nav.integrations')}</Link></li>
            <li><Link to={lp('/pricing')}>{t('nav.pricing')}</Link></li>
          </ul>
        </div>
        <div>
          <h5>{t('footer.company')}</h5>
          <ul>
            <li><Link to={lp('/about')}>{t('footer.about')}</Link></li>
            <li><Link to={lp('/customers')}>{t('nav.customers')}</Link></li>
            <li><Link to={lp('/careers')}>{t('footer.careers')}</Link></li>
            <li><Link to={lp('/contact')}>{t('nav.contact')}</Link></li>
          </ul>
        </div>
        <div>
          <h5>{t('footer.resources')}</h5>
          <ul>
            <li><Link to={lp('/blog')}>{t('nav.blog')}</Link></li>
            <li>{t('footer.docs')}</li>
            <li>{t('footer.changelog')}</li>
            <li><Link to={lp('/security')}>{t('footer.security')}</Link></li>
            <li><Link to={lp('/ai-data')}>{t('footer.aiData')}</Link></li>
            <li>{t('footer.status')}</li>
          </ul>
        </div>
      </div>
      <div className="rs-footer-bottom">
        <span>© {new Date().getFullYear()} Rush Studio Ltd.</span>
        <span><Link to={lp('/privacy')}>{t('footer.privacy')}</Link> · <Link to={lp('/terms')}>{t('footer.terms')}</Link> · <Link to={lp('/gdpr')}>{t('footer.dpa')}</Link> · <Link to={lp('/ai-data')}>{t('footer.aiData')}</Link></span>
      </div>
    </div>
  </footer>
  );
};

// Hook: scroll-reveal animations (IntersectionObserver). Returns nothing; idempotent.
export const useRushReveal = () => {
  React.useEffect(() => {
    // Deterministic reveal: a rAF-throttled viewport check on scroll/resize
    // plus a MutationObserver sweep. An IntersectionObserver-only version
    // silently left sections at opacity 0 whenever elements mounted after
    // the one-time registration — e.g. blocks that render null until their
    // i18n namespace loads, or a param-only route change (/roles/a →
    // /roles/b) that swaps content without remounting the page component.
    const sweep = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      document.querySelectorAll('.reveal:not(.in-view), .stagger:not(.in-view)').forEach((el) => {
        const r = el.getBoundingClientRect();
        // Reveal once the element pokes into the bottom 92% of the viewport
        // (mirrors the old IO rootMargin '0px 0px -8% 0px') — or has been
        // scrolled past entirely, so jump-scrolls / restored scroll positions
        // can't strand sections above the viewport at opacity 0.
        if (r.top < vh * 0.92) el.classList.add('in-view');
      });
    };
    // setTimeout rather than requestAnimationFrame: rAF stalls entirely in
    // hidden/backgrounded documents, which would strand content at opacity 0.
    let pending = 0;
    const schedule = () => {
      if (pending) return;
      pending = window.setTimeout(() => { pending = 0; sweep(); }, 40);
    };

    sweep();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Belt-and-braces: embedded/backgrounded webviews can suppress scroll
    // events entirely, which would leave below-the-fold content invisible.
    const interval = window.setInterval(sweep, 1000);
    let mo;
    if (typeof MutationObserver !== 'undefined') {
      mo = new MutationObserver(schedule);
      mo.observe(document.body, { childList: true, subtree: true });
    }
    return () => {
      if (pending) clearTimeout(pending);
      clearInterval(interval);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (mo) mo.disconnect();
    };
  }, []);
};
